import { useMemo } from 'react';
import type { FC } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Line, Bar } from 'react-chartjs-2';
import { StatCard, ChartCard } from '../components/Cards';
import { groupMetrics } from '../utils/group-metrics';
import { baseChartOptions, lineDataset, barDataset, CHART_COLORS } from '../utils/charts';
import { formatCents } from '../utils/format';
import type { OpsContext } from '../types';

const Deposits: FC = () => {
  const { current, history, grouping } = useOutletContext<OpsContext>();
  const buckets = useMemo(() => groupMetrics(history, grouping), [history, grouping]);
  const labels = buckets.map((b) => b.label);

  const toDollars = (cents: number) => +(cents / 100).toFixed(2);

  const cumulativeData = {
    labels,
    datasets: [
      lineDataset('Deposits', buckets.map((b) => toDollars(b.totalDepositCents)), CHART_COLORS.deposits),
      lineDataset('Spend', buckets.map((b) => toDollars(b.totalSpendCents)), CHART_COLORS.spend),
      lineDataset('Balance', buckets.map((b) => toDollars(b.totalBalanceCents)), CHART_COLORS.balance),
    ],
  };

  const perPeriodData = {
    labels,
    datasets: [
      barDataset('Deposits', buckets.map((b) => toDollars(b.dailyDepositCents)), CHART_COLORS.deposits),
      barDataset('Spend', buckets.map((b) => toDollars(b.dailySpendCents)), CHART_COLORS.spend),
    ],
  };

  return (
    <>
      <div className="row row-cols-2 row-cols-md-4 g-2 mb-3">
        <StatCard label="Total deposits" value={formatCents(current?.totalDepositCents ?? 0)} variant="success" />
        <StatCard label="Total spend" value={formatCents(current?.totalSpendCents ?? 0)} variant="warning" />
        <StatCard label="Total balance" value={formatCents(current?.totalBalanceCents ?? 0)} variant="info" />
        <StatCard label="Users w/ first deposit" value={(current?.usersWithFirstDeposit ?? 0).toString()} variant="primary" />
      </div>

      {buckets.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <ChartCard title="Cumulative (USD)">
            <Line options={baseChartOptions} data={cumulativeData} />
          </ChartCard>
          <ChartCard title="Per period (USD)">
            <Bar options={baseChartOptions} data={perPeriodData} />
          </ChartCard>
        </>
      )}
    </>
  );
};

const EmptyState: FC = () => (
  <div className="text-center text-muted py-5">
    <p>No metrics data in the selected range. Metrics are computed daily at 03:00 UTC.</p>
  </div>
);

export default Deposits;
